import { ArrowLeft, Facebook, Instagram, Twitter, Upload } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileService } from "../services/fileService";
import { storeService } from "../services/storeService";

interface FormData {
  brandName: string;
  tagLine: string;
  subTagLine: string;
  logo: File | null;
  logoUrl: string | null;
  banner: File | null;
  bannerUrl: string | null;
  socialLinks: {
    facebook: string;
    instagram: string;
    twitter: string;
  };
}

const EditStoreConfig = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<FormData>({
    brandName: "",
    tagLine: "",
    subTagLine: "",
    logo: null,
    logoUrl: null,
    banner: null,
    bannerUrl: null,
    socialLinks: {
      facebook: "",
      instagram: "",
      twitter: ""
    }
  });

  // Fetch initial data
  useEffect(() => {
    const fetchStoreData = async () => {
      try {
        const response: any = await storeService.getStore();
        setFormData({
          brandName: response.brandName || "",
          tagLine: response.tagLine || "",
          subTagLine: response.subTagLine || "",
          logo: null,
          logoUrl: response.logoUrl,
          banner: null,
          bannerUrl: response.bannerUrl,
          socialLinks: {
            facebook: response.socialLinks?.facebook || "",
            instagram: response.socialLinks?.instagram || "",
            twitter: response.socialLinks?.twitter || ""
          }
        });
      } catch (error) {
        console.error("Error fetching store data:", error);
        alert("Failed to load store configuration");
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoreData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let logoUrl = formData.logoUrl;
      let bannerUrl = formData.bannerUrl;

      if (formData.logo) {
        const logoResponse = await FileService.uploadFiles([formData.logo]);
        logoUrl = logoResponse.files[0].url;
      }

      if (formData.banner) {
        const bannerResponse = await FileService.uploadFiles([formData.banner]);
        bannerUrl = bannerResponse.files[0].url;
      }

      await storeService.updateStore({
        brandName: formData.brandName,
        tagLine: formData.tagLine,
        subTagLine: formData.subTagLine,
        logoUrl,
        bannerUrl,
        socialLinks: formData.socialLinks
      });

      navigate("/admin/store");
    } catch (error) {
      console.error("Error updating store configuration:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLinkChange = (platform: keyof typeof formData.socialLinks) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: e.target.value
      }
    }));
  };

  const handleFileUpload = (type: 'logo' | 'banner') => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log(file);

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPG, PNG, or GIF)');
      return;
    }

    // Validate file size (2MB for logo, 5MB for banner)
    const maxSize = type === 'logo' ? 2 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File size too large. Maximum size is ${maxSize / 1024 / 1024}MB`);
      return;
    }

    // Update form data with the new file and create a temporary URL
    setFormData((prev: FormData) => ({
      ...prev,
      [type]: file,
      [`${type}Url`]: URL.createObjectURL(file)
    }));
  };

  const handleRemoveFile = (type: 'logo' | 'banner') => {
    setFormData((prev: FormData) => ({
      ...prev,
      [type]: null,
      [`${type}Url`]: null
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="flex items-center gap-3 bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100 w-full max-w-sm">
          <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-2 border-blue-600 border-t-transparent"></div>
          <p className="text-sm sm:text-base text-gray-700 font-medium">Loading store configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24 sm:pb-20">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/store")}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Edit Store Configuration</h1>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 sm:mt-8">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Left Column - Store Details and Assets */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            {/* Store Information Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Store Information</h2>
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5 sm:mb-2">Brand Name</label>
                    <input
                      type="text"
                      value={formData.brandName}
                      onChange={e => setFormData(prev => ({ ...prev, brandName: e.target.value }))}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="Enter your brand name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5 sm:mb-2">TagLine</label>
                    <input
                      type="text"
                      value={formData.tagLine}
                      onChange={e => setFormData(prev => ({ ...prev, tagLine: e.target.value }))}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="Enter your store tagLine"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5 sm:mb-2">SubTagLine</label>
                    <input
                      type="text"
                      value={formData.subTagLine}
                      onChange={e => setFormData(prev => ({ ...prev, subTagLine: e.target.value }))}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="Enter your store subTagLine"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Store Assets Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Store Assets</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                  {/* Logo Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2 sm:mb-3">Store Logo</label>
                    <div className="flex justify-center p-4 sm:p-6 border border-gray-200 rounded-xl bg-gray-50">
                      {formData.logoUrl ? (
                        <div className="relative">
                          <img src={formData.logoUrl} alt="Store Logo" className="h-32 w-32 sm:h-40 sm:w-40 object-contain" />
                          <button
                            type="button"
                            onClick={() => handleRemoveFile('logo')}
                            className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Upload className="mx-auto h-8 w-8 sm:h-10 sm:w-10 text-gray-400 mb-2 sm:mb-3" />
                          <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-500 font-medium">
                            <span>Upload Logo</span>
                            <input
                              type="file"
                              className="sr-only"
                              accept="image/*"
                              onChange={handleFileUpload('logo')}
                            />
                          </label>
                          <p className="mt-1 text-xs text-gray-500">Recommended size: 400x400px</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Banner Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2 sm:mb-3">Store Banner</label>
                    <div className="flex justify-center p-4 sm:p-6 border border-gray-200 rounded-xl bg-gray-50">
                      {formData.bannerUrl ? (
                        <div className="relative w-full">
                          <img src={formData.bannerUrl} alt="Store Banner" className="w-full aspect-[21/9] object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => handleRemoveFile('banner')}
                            className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className="text-center w-full py-8 sm:py-12">
                          <Upload className="mx-auto h-8 w-8 sm:h-10 sm:w-10 text-gray-400 mb-2 sm:mb-3" />
                          <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-500 font-medium">
                            <span>Upload Banner</span>
                            <input
                              type="file"
                              className="sr-only"
                              accept="image/*"
                              onChange={handleFileUpload('banner')}
                            />
                          </label>
                          <p className="mt-1 text-xs text-gray-500">Recommended size: 1920x400px</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Social Media */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden lg:sticky lg:top-24">
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Social Media</h2>
                <div className="space-y-4">
                  {Object.entries(formData.socialLinks).map(([platform, url]) => (
                    <div key={platform} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-600 capitalize">
                        {platform}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          {platform === "facebook" && <Facebook className="w-5 h-5 text-blue-600" />}
                          {platform === "instagram" && <Instagram className="w-5 h-5 text-pink-600" />}
                          {platform === "twitter" && <Twitter className="w-5 h-5 text-blue-400" />}
                        </div>
                        <input
                          type="url"
                          value={url}
                          onChange={handleSocialLinkChange(platform as keyof typeof formData.socialLinks)}
                          className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                          placeholder={`Enter ${platform} URL`}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {platform === "facebook" && "Example: https://facebook.com/yourpage"}
                        {platform === "instagram" && "Example: https://instagram.com/yourhandle"}
                        {platform === "twitter" && "Example: https://twitter.com/yourhandle"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button - Fixed at bottom */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 py-3 sm:py-4 px-4 sm:px-6 lg:px-8 z-[100] shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
            <div className="max-w-7xl mx-auto flex flex-row justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate("/admin/store")}
                className="flex-1 sm:flex-none sm:w-auto px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 sm:flex-none sm:w-auto px-4 py-2.5 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {/* Add padding to prevent content from being hidden behind fixed buttons */}
          <div className="h-24 sm:h-28"></div>
        </form>
      </div>
    </div>
  );
};

export default EditStoreConfig; 